import { useState, useEffect, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Brain,
  User,
  Megaphone,
  Settings2,
} from "lucide-react";
import {
  useContactDetailFull,
  useContactOutreachMessages,
} from "@/hooks/useContactDetail";
import {
  useContactCampaigns,
  useContactOpportunities,
} from "@/hooks/useContactIntelligence";
import { ContactHeader } from "./ContactHeader";
import { ContactContext } from "./ContactContext";
import { ContactIntelTab } from "./ContactIntelTab";
import { ContactProfileTab } from "./ContactProfileTab";
import { ContactCampaignsTab } from "./ContactCampaignsTab";
import { ContactActionsTab } from "./ContactActionsTab";

// ── Types ────────────────────────────────────────────────────────────

export type ContactModalMode = "default" | "email" | "linkedin";

export interface UnifiedContactModalProps {
  contactId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: ContactModalMode;
  campaignId?: string | null;
  campaignName?: string | null;
  panel?: ReactNode;
  fallbackData?: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    job_title?: string | null;
    company_name?: string | null;
    email?: string | null;
    linkedin_url?: string | null;
    location?: string | null;
    headline?: string | null;
  } | null;
}

// ── Component ────────────────────────────────────────────────────────

export function UnifiedContactModal({
  contactId,
  open,
  onOpenChange,
  mode = "default",
  campaignId,
  campaignName,
  panel,
  fallbackData,
}: UnifiedContactModalProps) {
  const { data: contact, isLoading } = useContactDetailFull(open ? contactId : null);
  const { data: campaigns } = useContactCampaigns(open ? contactId : null);
  const { data: opportunities } = useContactOpportunities(open ? contactId : null);
  const { data: outreachMessages } = useContactOutreachMessages(open ? contactId : null);

  const [activeTab, setActiveTab] = useState("intel");

  useEffect(() => {
    if (open) setActiveTab("intel");
  }, [open]);

  const isUnmatched = !contactId || (!isLoading && !contact);
  const hasPanel = mode !== "default" && !!panel;

  // Width: default = max-w-4xl, with panel = max-w-7xl
  const widthClass = hasPanel ? "max-w-7xl" : "max-w-5xl";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${widthClass} max-h-[92vh] flex flex-col p-0 overflow-hidden`}>
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <div className="shrink-0">
              <ContactHeader
                contact={contact ?? null}
                fallbackData={fallbackData}
              />

              {contact && <ContactContext contact={contact} />}
            </div>

            {/* Main body: tabs + optional panel side-by-side */}
            <div className={`flex-1 min-h-0 overflow-hidden flex ${hasPanel ? "flex-row" : "flex-col"}`}>
              {/* Left: Contact tabs */}
              <div className={`flex-1 min-h-0 flex flex-col ${hasPanel ? "border-r" : ""}`}>
                {isUnmatched ? (
                  <div className="flex-1 overflow-auto p-6">
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <User className="h-10 w-10 mb-3" />
                      <p className="text-sm font-medium">Unmatched Contact</p>
                      <p className="text-xs mt-1 text-center max-w-sm">
                        This contact hasn't been matched to a contact record yet.
                        Only basic campaign data is available.
                      </p>
                    </div>
                  </div>
                ) : contact && (
                  <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="flex-1 flex flex-col min-h-0"
                  >
                    <TabsList className="mx-6 mt-2 w-fit">
                      <TabsTrigger value="intel" className="text-sm">
                        <Brain className="h-3.5 w-3.5 mr-1.5" />
                        Intel
                      </TabsTrigger>
                      <TabsTrigger value="profile" className="text-sm">
                        <User className="h-3.5 w-3.5 mr-1.5" />
                        Profile
                      </TabsTrigger>
                      <TabsTrigger value="campaigns" className="text-sm">
                        <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                        Campaigns
                        {(campaigns?.length ?? 0) > 0 && (
                          <Badge variant="secondary" className="ml-1.5 text-[11px] px-1.5 py-0">
                            {campaigns?.length}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="actions" className="text-sm">
                        <Settings2 className="h-3.5 w-3.5 mr-1.5" />
                        Actions
                      </TabsTrigger>
                    </TabsList>

                    <div className="flex-1 min-h-0 overflow-hidden">
                      <TabsContent value="intel" className="mt-0 h-full">
                        <ScrollArea className="h-full">
                          <div className="px-6 pb-6 pt-4">
                            <ContactIntelTab contact={contact} />
                          </div>
                        </ScrollArea>
                      </TabsContent>

                      <TabsContent value="profile" className="mt-0 h-full">
                        <ScrollArea className="h-full">
                          <div className="px-6 pb-6 pt-4">
                            <ContactProfileTab contact={contact} />
                          </div>
                        </ScrollArea>
                      </TabsContent>

                      <TabsContent value="campaigns" className="mt-0 h-full">
                        <ScrollArea className="h-full">
                          <div className="px-6 pb-6 pt-4">
                            <ContactCampaignsTab
                              campaigns={campaigns}
                              opportunities={opportunities}
                              outreachMessages={outreachMessages}
                            />
                          </div>
                        </ScrollArea>
                      </TabsContent>

                      <TabsContent value="actions" className="mt-0 h-full">
                        <ScrollArea className="h-full">
                          <div className="px-6 pb-6 pt-4">
                            <ContactActionsTab
                              campaignCount={campaigns?.length || 0}
                              messageCount={outreachMessages?.length || 0}
                              opportunityCount={opportunities?.length || 0}
                            />
                          </div>
                        </ScrollArea>
                      </TabsContent>
                    </div>
                  </Tabs>
                )}
              </div>

              {/* Right: Add-on panel (email read-only or LinkedIn editable) */}
              {hasPanel && (
                <div className="w-[480px] xl:w-[560px] min-h-0 flex flex-col">
                  <ScrollArea className="flex-1">
                    {panel}
                  </ScrollArea>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
